/**
 * Maneuver grade filter thresholds in settings: -1 = All (grade > -1), 1 = >1, 2 = >2.
 * The former ">0" option (stored as 0) was removed from the UI; map legacy values to All (-1).
 */
export function normalizeManeuverGradeFilterThreshold(grade: number): number {
  return grade === 0 ? -1 : grade;
}

export function normalizeManeuverGradeFilterThresholdList(grades: number[]): number[] {
  return grades.map(normalizeManeuverGradeFilterThreshold);
}

/** Settings value for the All grade chip (grade > -1). */
export const MANEUVER_GRADE_ALL = -1;

/** Empty or All (-1). A stored 0 is the retired ">0" chip, which still hides grade 0. */
export function isManeuverGradeAll(grades: readonly number[]): boolean {
  if (grades.length === 0) return true;
  return grades.every((grade) => Number.isFinite(grade) && grade <= MANEUVER_GRADE_ALL);
}

export type MapClickGradeDecision = "wait" | "keep" | "open-all";

function rowEventType(row: Record<string, unknown>): string {
  return String(row.event_type ?? row.Event_type ?? "").trim().toUpperCase();
}

/**
 * A map click whose maneuver is not in the page's filtered rows should select All.
 * Wait while the loaded rows are still a different maneuver type, so a type change
 * can finish its own fetch before All replaces a grade filter that already includes it.
 */
export function decideMapClickGrade(input: {
  eventId: number;
  eventType: string;
  grades: readonly number[];
  allRows: ReadonlyArray<Record<string, unknown>>;
  shownIds: ReadonlyArray<number>;
}): MapClickGradeDecision {
  if (isManeuverGradeAll(input.grades)) return "keep";
  const eventId = Number(input.eventId);
  if (!Number.isFinite(eventId) || eventId <= 0) return "keep";

  const shown = new Set(
    input.shownIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
  );
  if (shown.has(eventId)) return "keep";

  const wanted = input.eventType.trim().toUpperCase();
  const typedRows = input.allRows.filter((row) => rowEventType(row) !== "");
  if (wanted !== "" && typedRows.length > 0 && !typedRows.some((row) => rowEventType(row) === wanted)) {
    return "wait";
  }
  return "open-all";
}
